import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import { FavouriteMenusReports } from './FavouriteMenusReports';
import { FavouriteMenusFields } from './FavouriteMenusFields';
import favouriteMenuService from '../../service/favouriteMenu.service';
import masterDataService from '../../service/masterData.service';
import appService from '../../service/app.service';
import { DocumentTypeRoutes } from '../../config/DocumentType';
import newDocumentTypeService from '../../service/newDocumentType.service';

export const FavouriteMenusPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user, setFavouriteMenu, updateFavouriteMenu, deleteFavouriteMenu } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [menuData, setMenuData] = useState([]);

  const def = {
    name: '',
    subMenuUrl: '',
    icon: 'Home',
    showOnHomePage: false,
    showOnNavbar: false,
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      getAllAutoCompleteData();
    }
  }, [user]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('favouriteMenus');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    subMenuUrl: Yup.string().required(),
    showOnHomePage: Yup.boolean(),
    showOnNavbar: Yup.boolean(),
  });

  const getAllAutoCompleteData = () => {
    Promise.all([
      appService.getAllSubMenus(),
      masterDataService.biReportsToForm(),
      masterDataService.dashboardsToForm(),
      newDocumentTypeService.allDocumentTypes(),
    ])
      .then(([submenuData, biReportData, dashboardData, docTypes]) => {
        //@TODO maybe roles
        const formattedSubmenus = submenuData
          .filter((submenu) => {
            return submenu.roleSubmenuPermission?.some((p) => p.roleId === user.roleId);
          })
          .map((submenu) => ({
            label: submenu.subMenuName,
            value: submenu.url,
          }));

        const formattedBiReports = biReportData
          .filter((report) => {
            return report.biRolePermission?.some((p) => p.roleId === user.roleId);
          })
          .map((report) => ({
            label: `R - ${report.name}`,
            value: `bi/biPage/${report.id}`,
          }));

        const formattedDashboards = dashboardData
          .filter((dashboard) => {
            return dashboard.dashboardPermission?.some((p) => p.roleId === user.roleId);
          })
          .map((dashboard) => ({
            label: `D - ${dashboard.name}`,
            value: `dashboard/${dashboard.id}`,
          }));

        const formattedDoctypes = docTypes
          .filter((docType) => {
            return docType.roleDocPermissions?.some((p) => p.roleId === user.roleId);
          })
          .map((docType) => {
            const route = DocumentTypeRoutes[docType.documentTypeId];

            return {
              label: `B - ${docType.name}`,
              value: `${route}`,
            };
          });

        const mergedData = [...formattedSubmenus, ...formattedBiReports, ...formattedDashboards, ...formattedDoctypes];

        setMenuData(mergedData);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  const valuesToForm = () => {
    const { id, name, subMenuUrl, icon, showOnHomePage, showOnNavbar } = selectedValues;

    setInitValues({
      id,
      name,
      subMenuUrl,
      icon,
      showOnHomePage,
      showOnNavbar,
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
    };

    favouriteMenuService.updateData(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
      updateFavouriteMenu(data);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
    };

    favouriteMenuService
      .createData(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setFavouriteMenu(data);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    favouriteMenuService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
      deleteFavouriteMenu(selectedValues.id);
    });
  };

  return (
    <>
      <FormBase
        name="FavouriteMenu"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <FavouriteMenusFields viewField={viewField} menuData={menuData} />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <FavouriteMenusReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            user={user}
            menuData={menuData}
            setFavouriteMenu={setFavouriteMenu}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
